import React from 'react';
import clsx from 'clsx';
import { TriangleAlert } from 'lucide-react';
import type { ConnectionFailureStep, SoftphoneFailureNotice } from '../../services/softphoneRecovery';
import { getConnectionFailureGuideline } from '../../services/connectionFailureGuidelines';

interface ConnectConnectionFailedPanelProps {
    failure: SoftphoneFailureNotice | null;
}

const GUIDELINE_ICON_COLOR = 'rgb(163, 162, 66)';
const GUIDELINE_ICON_BOX = { width: '18px', height: '18px' } as const;

function ChromeGuidelineIcon() {
    return (
        <span
            className="cw-connection-failed-step-icon flex-shrink-0"
            style={GUIDELINE_ICON_BOX}
            aria-hidden="true"
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 48 48">
                <path fill="#4caf50" d="M44,24c0,11.044-8.956,20-20,20S4,35.044,4,24S12.956,4,24,4S44,12.956,44,24z" />
                <path fill="#ffc107" d="M24,4v20l8,4l-8.843,16c0.317,0,0.526,0,0.843,0c11.053,0,20-8.947,20-20S35.053,4,24,4z" />
                <path fill="#f44336" d="M41.84,15H24v13l-3-1L7.16,13.26H7.14C10.68,7.69,16.91,4,24,4C31.8,4,38.55,8.48,41.84,15z" />
                <path fill="#dd2c00" d="M7.158,13.264l8.843,14.862L21,27L7.158,13.264z" />
                <path fill="#558b2f" d="M23.157,44l8.934-16.059L28,25L23.157,44z" />
                <path fill="#f9a825" d="M41.865,15H24l-1.579,4.58L41.865,15z" />
                <path fill="#fff" d="M33,24c0,4.969-4.031,9-9,9s-9-4.031-9-9s4.031-9,9-9S33,19.031,33,24z" />
                <path fill="#2196f3" d="M31,24c0,3.867-3.133,7-7,7s-7-3.133-7-7s3.133-7,7-7S31,20.133,31,24z" />
            </svg>
        </span>
    );
}

function ConnectionFailureStepRow({ step }: { step: ConnectionFailureStep }) {
    return (
        <div className="cw-connection-failed-step">
            {step.icon === 'chrome' ? (
                <ChromeGuidelineIcon />
            ) : (
                <i
                    className={clsx('bi', step.icon, 'cw-connection-failed-step-icon flex-shrink-0')}
                    style={{ color: GUIDELINE_ICON_COLOR, ...GUIDELINE_ICON_BOX }}
                    aria-hidden="true"
                />
            )}
            <span className="cw-connection-failed-step-text">{step.text}</span>
        </div>
    );
}

export function ConnectConnectionFailedPanel({ failure }: ConnectConnectionFailedPanelProps) {
    const guideline = getConnectionFailureGuideline(failure?.errorType ?? 'other');
    const summary = failure?.summary || guideline.summary;

    return (
        <div
            className="cw-connection-failed"
            role="alert"
            aria-live="assertive"
            aria-labelledby="cw-connection-failed-title"
        >
            <div className="cw-connection-failed-hero">
                <TriangleAlert
                    size={52}
                    strokeWidth={2}
                    color="#D7383C"
                    aria-hidden="true"
                />
                <h4 id="cw-connection-failed-title" className="cw-connection-failed-title">
                    Connection failed
                </h4>
                <p className="cw-connection-failed-summary">{summary}</p>
            </div>

            <div className="cw-connection-failed-steps">
                <div className="cw-connection-failed-steps-label">What to try</div>
                {guideline.steps.map((step) => (
                    <ConnectionFailureStepRow key={step.id} step={step} />
                ))}
            </div>

            <p className="cw-connection-failed-footer">
                When you are ready, switch your status to Available to receive your next call.
            </p>
        </div>
    );
}
